import { AbsoluteFill } from "remotion";
import AnimatedImage from "../AnimatedImage.tsx";
import { CompositionTimings, msToFrames } from "../../utils/timing.ts";
import {
  getAnimatedImageConfig,
  getElementsByScene,
} from "@repo/api/utils/composition";
import { CompositionDto, I2VCompositionProps } from "@repo/api/editor";
import ElementsLayer from "../Element/ElementsLayer.tsx";
import { TransitionSeries } from "@remotion/transitions";
import { Fragment } from "react";
import { ImageTransitionBuilders } from "../ImageTransitionBuilder/ImageTransitionBuilder.ts";
import ImageTransitionWrapper from "../ImageTransitionBuilder/ImageTransitionWrapper.tsx";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { SourceImageDto } from "@repo/api/source-image";

type Props = {
  composition: CompositionDto;
  sourceImages: Record<string, SourceImageDto>;
  animatedImages: Record<string, AnimatedImageDto>;
  timings: CompositionTimings;
  assetMedias: I2VCompositionProps["assetMedias"];
  voiceOvers: I2VCompositionProps["voiceOvers"];
  fps: number;
  isPlaying: boolean;
};

export default function MainScene({
  composition,
  sourceImages,
  animatedImages,
  assetMedias,
  timings,
  fps,
  isPlaying,
}: Props) {
  return (
    <AbsoluteFill>
      <TransitionSeries from={0}>
        {composition.mainSceneTimeline.map((timelineItem, index) => {
          const sourceImage =
            sourceImages[timelineItem.startSourceImageExternalId];
          const animatedImage = timelineItem.animatedImageExternalId
            ? animatedImages[timelineItem.animatedImageExternalId]
            : undefined;

          const timing = timings.mainScene.compositionImageTimings[index]!;

          const isFirst = index === 0;
          const isLast = index === composition.mainSceneTimeline.length - 1;

          const transition =
            !isFirst && timelineItem.entryTransition
              ? ImageTransitionBuilders.get(timelineItem.entryTransition.kind)
                  ?.transition
              : null;

          return (
            <Fragment key={sourceImage!.externalId}>
              {!!transition && (
                <TransitionSeries.Transition
                  presentation={transition.buildPresentation({
                    imageTransition: timelineItem.entryTransition!,
                    fps,
                  })}
                  timing={transition.buildTiming({
                    imageTransition: timelineItem.entryTransition!,
                    fps,
                  })}
                />
              )}
              <TransitionSeries.Sequence
                key={parseTimelineItemSequenceKey(timelineItem, animatedImage)}
                premountFor={msToFrames(2000, fps)}
                durationInFrames={timing.durationInFrames}
              >
                <ImageTransitionWrapper
                  entryTransition={
                    isFirst ? null : (timelineItem.entryTransition ?? null)
                  }
                  exitTransition={
                    isLast
                      ? null
                      : (composition.mainSceneTimeline[index + 1]
                          ?.entryTransition ?? null)
                  }
                  durationInFrames={timing.durationInFrames}
                >
                  <AnimatedImage
                    aspectRatio={composition.aspectRatio}
                    timelineItem={timelineItem}
                    sourceImage={sourceImage!}
                    animatedImage={animatedImage}
                    fps={fps}
                  />
                </ImageTransitionWrapper>
              </TransitionSeries.Sequence>
            </Fragment>
          );
        })}
      </TransitionSeries>

      <ElementsLayer
        elements={getElementsByScene(composition, "mainScene")}
        elementTimings={timings.mainScene.elementTimings}
        assetMedias={assetMedias}
        sceneType="mainScene"
        isPlaying={isPlaying}
      />
    </AbsoluteFill>
  );
}

// Sequence key fixes a bug where Remotion ignores playback speed if duration is changed when video is playing.
function parseTimelineItemSequenceKey(
  timelineItem: CreativeProjectTimelineItem,
  animatedImage: AnimatedImageDto | undefined,
) {
  if (!timelineItem.animatedImageExternalId) {
    return timelineItem.externalId;
  }

  const config = getAnimatedImageConfig(timelineItem);
  const src = config.reverse
    ? animatedImage?.backwardSrc
    : animatedImage?.forwardSrc;

  return `${timelineItem.externalId}-${config.duration ?? 2.5}-${config.reverse ? "reverse" : "forward"}-${config.playbackSpeed ?? 1}-${src ?? "no-src"}`;
}
